import { Component } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { MediaObserver } from '@angular/flex-layout';
import { combineLatest, Subject, Subscription, TeardownLogic } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { RoutePath } from './core/routing/routes';
import { AuthService, BUILD_TIME, ErrorService, MultiplePathwaysSerivce, ScreenSizeService, StyleManager, UserService, VERSION_NUMBER } from './core/services';
import { TestConnectionPacket } from './core/services/authentication/auth';
import { IProgramInformation, IChallengeTypeInfo, IDenialTypeInfo, IPlacementTeamTypeInfo, IEligibleProgramStartDateInfo, ISchoolInfo, IExtendedStudentInfo } from './core/services/mainapp/multiplePathways';
import { UserInfo } from './core/services/user/user';
import { DatePipe } from '@angular/common';

interface StatusReturn {
    AllYears: string[];
    AvailPrograms: IProgramInformation[];
    AvailChallTypes: IChallengeTypeInfo[];
    AvailDenTypes: IDenialTypeInfo[];
    AvailAppSchoolYears: string[];
    CurrentAppSchoolYear: string;
    CurrentYear: string;
    devsite?: boolean;
    TestConn: TestConnectionPacket;
}


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent {
    private _subrelease: Subscription = new Subscription();
    set subrelease(sub: TeardownLogic) {
        this._subrelease.add(sub);
    }
    
    BuildNotes?: string;
    BuildTime?: string;
    CurrentApplicationSchoolYear?: string | null;
    DevSite?: boolean;
    LastSizeCode?: string;
    LastSmallDisplay?: boolean;
    SizeCode?: string;
    SmallSize?: boolean;
    SmallDisplay?: boolean;
    UpdateAvailable?: boolean;
    User?: UserInfo | null;
    VersionLoaded?: string;
    ViewReady?: boolean;

    constructor(
        private router: Router,
        private authService: AuthService,
        private datePipe: DatePipe,
        private errorService: ErrorService,
        private media: MediaObserver,
        private REALService: MultiplePathwaysSerivce,
        private styleManager: StyleManager,
        private screenSizeSvc: ScreenSizeService, 
        private userService: UserService, 
    ) {
        const self = this;
        self.styleManager.loadDarkTheme();

        const btime = (BUILD_TIME && new Date(BUILD_TIME)) || new Date();
        self.BuildTime = this.datePipe.transform(btime, 'MMM. dd, yyyy h:mm a') || '';

        // Subscribe to media changes
        self.subrelease = self.screenSizeSvc.ScreenSizeSub.subscribe((size) => {
            self.SmallDisplay = size.IsSmallDisplay;
            self.SizeCode = size.Alias;

            self.LastSmallDisplay = self.SmallDisplay;
        });

        // Subscribe to user object changes
        self.subrelease = self.userService.UserSubject.subscribe((_user) => {
            self.User = _user;
        });

        self.SmallDisplay = self.screenSizeSvc.ScreenSize.IsSmallDisplay;
        self.LastSmallDisplay = self.SmallDisplay;
        self.SizeCode = self.screenSizeSvc.ScreenSize.Alias;

        // Subscribe to system online status updates. If the system goes offline, this may cause us to reroute to the offline route.
        self.subrelease = self.authService.SystemOnlineSubject.pipe(distinctUntilChanged((p, c) => p.Status === c.Status)).subscribe(
            (status: TestConnectionPacket) => {
                if (status?.Status) {
                    // Load everything needed for the app to run initially.
                    combineLatest([
                        self.REALService.GetAllSchoolYears(),
                        self.REALService.GetAvailablePrograms(),
                        self.REALService.GetAvailableChallengeTypes(),
                        self.REALService.GetAvailableDenialTypes(),
                        self.REALService.GetAvailableApplicationSchoolYears(),
                        self.REALService.GetCurrentApplicationSchoolYear(),
                        self.REALService.GetCurrentSchoolYear(),
                        self.REALService.DevSiteSubject,
                    ]).pipe(
                        map(([
                            AllYears,
                            AvailPrograms,
                            AvailChallTypes,
                            AvailDenTypes,
                            AvailAppSchoolYears,
                            CurrentAppSchoolYear,
                            CurrentYear,
                            devsite,
                        ]) => ({
                            AllYears,
                            AvailPrograms,
                            AvailChallTypes,
                            AvailDenTypes,
                            AvailAppSchoolYears,
                            CurrentAppSchoolYear,
                            CurrentYear,
                            devsite,
                        } as StatusReturn)))
                        .subscribe({
                            next: (result: StatusReturn) => {
                                
                                self.CurrentApplicationSchoolYear = result.CurrentAppSchoolYear;

                                // By default, start the app with this as the selected school year until it is changed by the user.
                                if ( 
                                    self.REALService._SelectedSchoolYear == null || 
                                    self.REALService._SelectedSchoolYear == "") 
                                {
                                    self.REALService.SetSelectedSchoolYear(self.CurrentApplicationSchoolYear);
                                }

                                self.ViewReady = true;

                                // If the current user has no auth token, we can now go to the login route to handle this.
                                if (!self.userService?.User?.AuthToken) {
                                    self.router.navigate([RoutePath.Login]);
                                }
                            },
                            error: (err) => {
                                self.ViewReady = true;
                                self.errorService.HandleServiceError(err);
                            },
                        });
                } else {
                    // If the system is offline, AND if there is no current user authtoken, go to the down/offline route.
                    self.ViewReady = true;

                    if (!self.userService?.User?.AuthToken) {
                        self.router.navigate([RoutePath.Offline]);
                    }
                }
            }
        );
}

    ngOnDestroy(): void {
        this._subrelease.unsubscribe();
        // this.authService.CancelAuthTokenRefreshing();
    }

    ngOnInit(): void {
        const self = this;

        // Conduct an initial test of the connection, to set the proper status.
        this.authService.TestConnection().subscribe({
            next: (result) => {
                this.authService.SetSystemOnlineStatus(result);
            },
            error: (err) => {
                // A failure would indicate the system is offline.
                this.authService.SetSystemOnlineStatus({
                    Status: false,
                    SystemMessage: '',
                    PathwaysVersion: VERSION_NUMBER,
                });
            }
        });
    }
}
